import React, { ReactNode, useState } from "react";
import { Select, Card, DatePicker } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Area,
  AreaChart,
} from "recharts";
import { useGetOffTakerDashboardValues, useOrderList } from "../api/order";
import StatCard from "./cards/stat-card/StatCard";
import { ProductOutlined } from "@ant-design/icons";
import LoadingScreen from "./LoadingScreen";
import dayjs from "dayjs";
import CustomersTable from "../components/tables/CustomersTable";
import { useNavigate } from "react-router-dom";
import ContractsTable from "./tables/ContractsTable";
import OffTakersTable from "./tables/OffTakersTable";

import OrdersTable from "../components/tables/order/OrdersTable";
const { Option } = Select;

// Define the Order interface
interface Order {
  id: number;
  orgName: string;
  product: string;
  orderAmount: number;
  dueDate: string;
  startingDate: string;
  tags: string;
  status:
    | "ACCEPTED"
    | "DELIVERED"
    | "INPROGRESS"
    | "PENDING"
    | "REJECTED"
    | "COMPLETED";
  createdDate: string;
}

const OffTakerOrderDashboard = () => {
  const { data: orders, isLoading } = useOrderList();
  const { data: dashboardValues } = useGetOffTakerDashboardValues();
  const [tableIndex, setTableIndex] = useState<number>(0);
  const navigate = useNavigate();
  const ordersArray = Array.isArray(orders?.content)
    ? (orders?.content as any[])
    : [];

  // State with TypeScript types
  const [selectedStatus, setSelectedStatus] = useState<
    | "ACCEPTED"
    | "DELIVERED"
    | "INPROGRESS"
    | "PENDING"
    | "REJECTED"
    | "COMPLETED"
    | "All"
    | undefined
  >("All");
  const [selectedOrgName, setSelectedOrgName] = useState<string | "All">("All");
  const [selectedProduct, setSelectedProduct] = useState<string | "All">("All");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  if (isLoading)
    return (
      <div>
        <LoadingScreen />
      </div>
    );
  if (ordersArray && ordersArray.length === 0)
    return <div>No order data available</div>;
  // Extract unique values for filters
  const uniqueStatuses = [
    "All" as const,
    ...Array.from(new Set(ordersArray.map((o) => o.status))),
  ];
  const uniqueOrgNames = [
    "All" as const,
    ...Array.from(new Set(ordersArray.map((o) => o.orgName))),
  ];
  const uniqueProducts = [
    "All" as const,
    ...Array.from(new Set(ordersArray.map((o) => o.product))),
  ];

  // Filter orders
  const filteredOrders = ordersArray?.filter(
    (o) =>
      (selectedStatus === "All" || o.status === selectedStatus) &&
      // (selectedOrgName === "All" || o.orgName === selectedOrgName) &&
      (selectedProduct === "All" || o.product === selectedProduct) &&
      (!selectedDate || dayjs(o.createdDate).isSame(selectedDate, "day"))
  );

  const topOrders = filteredOrders.slice(0, 20);

  // Prepare chart data
  const composedData = topOrders.map((o) => ({
    name: o.id.toString(),
    orderAmount: o.orderAmount,
    cumulativeAmount: topOrders
      .slice(0, topOrders.indexOf(o) + 1)
      .reduce((sum, order) => sum + order.orderAmount, 0),
  }));

  const statusDistribution = topOrders.reduce(
    (acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + o.orderAmount;
      return acc;
    },
    {
      ACCEPTED: 0,
      // DELIVERED: 0,
      // INPROGRESS: 0,
      PENDING: 0,
      REJECTED: 0,
      // COMPLETED: 0,
    } as Record<Order["status"], number>
  );

  const pieData = Object.entries(statusDistribution).map(([name, value]) => ({
    name,
    value,
  }));

  const CustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    name,
  }: {
    cx: number;
    cy: number;
    midAngle: number;
    innerRadius: number;
    outerRadius: number;
    percent: number;
    name: string;
  }) => {
    const radius = outerRadius * 1.1; // Adjust this multiplier to move the label further out
    const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
    const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

    // Only show label if percent is greater than 0 to avoid clutter
    if (percent < 0.1) return null;

    return (
      <text
        x={x}
        y={y}
        fill="#666"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const COLORS = [
    "#10B981",

    "#4F46E5",
    "#EF4444",
    "#F59E0B",

    "#8B5CF6",
    "#EC4899",
  ];
  const formatNumber = (value: number) =>
    new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);

  const renderTable = (): ReactNode => {
    switch (tableIndex) {
      case 1:
        return <OrdersTable searchText={searchText} />;
      case 2:
        return (
          <ContractsTable
            searchText={searchText}
            setSelectedFilterIndex={() => {}}
          />
        );
      case 3:
        return <CustomersTable searchText={searchText} />;
      case 4:
        return <OffTakersTable searchText={searchText} />;
      default:
        return null;
    }
  };
  return (
    <main className="flex flex-col gap-6 bg-white p-6">
      <div
        style={{
          display: "flex",
          width: "998px",
          gap: "12px",
          left: "243px",
          top: "98px",
          paddingLeft: "2px",
          paddingRight: "50px",
        }}
      >
        {/* Orders */}

        <div
          onClick={() => {
            setTableIndex(1);
            navigate("/orders");
          }}
          style={{
            cursor: "pointer",
            backgroundColor: "#C0FFCA",
            height: "134px",
            width: "325px",
            color: "grey",

            borderRadius: "20px",
            paddingTop: "20px",
            paddingLeft: "24px",
            paddingBottom: "20px",
            paddingRight: "24px",
            gap: "10px",
          }}
          title="Number of Orders"
        >
          <p style={{ color: "grey" }}>Number of Orders</p>
          <span style={{ fontSize: "50px", color: "black" }}>
            {dashboardValues?.data.data.data.numberOffOrders || 0}
          </span>
        </div>

        <div
          onClick={() => {
            setTableIndex(3);
            navigate("/customers");
          }}
          style={{
            cursor: "pointer",
            backgroundColor: "#EBFFB9",
            height: "134px",
            width: "325px",
            color: "grey",

            borderRadius: "20px",
            paddingTop: "20px",
            paddingLeft: "24px",
            paddingBottom: "20px",
            paddingRight: "24px",
            gap: "10px",
          }}
        >
          <p style={{ color: "grey" }}>Number of Customers</p>
          <span style={{ fontSize: "50px", color: "black" }}>
            {dashboardValues?.data.data.data.numberOffCustomers || 0}
          </span>
        </div>

        <div
          onClick={() => {
            setTableIndex(2);
            navigate("/contracts");
          }}
          style={{
            cursor: "pointer",
            backgroundColor: "#CEEDFF",
            height: "134px",
            width: "325px",
            color: "grey",

            borderRadius: "20px",
            paddingTop: "20px",
            paddingLeft: "24px",
            paddingBottom: "20px",
            paddingRight: "24px",
            gap: "10px",
          }}
        >
          <p style={{ color: "grey" }}>Number of Customers</p>
          <span style={{ fontSize: "50px", color: "black" }}>
            {dashboardValues?.data.data.data.numberOffContracts || 0}
          </span>
        </div>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <p
          style={{
            color: "rgba(0, 0, 0, 0.5)",
            fontSize: "16px",
            fontStyle: "Medium",
            font: "inter",
          }}
        >
          Filter by
        </p>
        {[
          {
            label: "Status",
            options: uniqueStatuses,
            state: selectedStatus,
            setter: setSelectedStatus,
            type: "select",
          },
          // {
          //   label: "Date",
          //   state: selectedDate,
          //   setter: setSelectedDate,
          //   type: "date",
          // },
          {
            label: "Product Name",
            options: uniqueProducts,
            state: selectedProduct,
            setter: setSelectedProduct,
            type: "select",
          },
        ].map(({ label, options, state, setter, type }) => (
          <div
            key={label}
            style={{
              top: "262px",
              left: "329px",
              paddingTop: "8px",
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingBottom: "8px",
              fontSize: "14px",
            }}
            className="flex items-center gap-6 border border-dashed border-gray-300 rounded-xl px-3 py-1.5 w-hug[115px] h-[36px]"
          >
            <span className="text-sm text-black whitespace-nowrap">
              {label}
            </span>
            <div className="w-px h-4 bg-gray-300 mx-2" />
            {type === "select" ? (
              <Select
                bordered={false}
                className="w-hug[40px] h-[30px] border-none outline-none text-sm bg-[#F4F4F5] rounded-sm "
                dropdownStyle={{ minWidth: "150px" }}
                value={state}
                onChange={(value) => setter(value as any)}
              >
                {options?.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <DatePicker
                className="w-full"
                value={selectedDate ? dayjs(selectedDate) : null}
                onChange={(date, dateString) => {
                  if (typeof dateString === "string") {
                    setSelectedDate(dateString);
                  } else {
                    setSelectedDate(null);
                  }
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Charts */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-white p-4">
          <h3 className="text-gray-500 mb-4 text-lg font-semibold px-4 pt-2">
            Order Performance Analysis
          </h3>

          <div className="h-72 flex items-center justify-center">
            {topOrders.length === 0 ? (
              <p className="text-gray-400 text-base">No data available</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={topOrders.map((o) => ({
                    name: o.id,
                    orderAmount: o.orderAmount,
                    cumulativeAmount: topOrders
                      .slice(0, topOrders.indexOf(o) + 1)
                      .reduce((sum, order) => sum + order.orderAmount, 0),
                  }))}
                  margin={{ top: 15, right: 20, bottom: 25, left: 10 }}
                >
                  <CartesianGrid stroke="#f3f4f6" strokeDasharray="3 3" />

                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#00000080", fontSize: 12 }}
                    label={{
                      value: "ORDER ID",
                      position: "insideBottom",
                      offset: -10,
                      style: { fill: "#444", fontSize: 13 },
                    }}
                  />

                  <YAxis
                    tickFormatter={formatNumber}
                    tick={{ fill: "#555", fontSize: 12 }}
                    label={{
                      value: "Amount",
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: "#444", fontSize: 13 },
                    }}
                  />

                  <Tooltip formatter={formatNumber} />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ paddingTop: 24 }}
                    className="text-sm"
                  />

                  {/* Bar for individual order amount */}
                  <Bar
                    dataKey="orderAmount"
                    fill="#555"
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                    name="Individual Amount"
                  />

                  {/* Area for cumulative total */}
                  <Area
                    type="monotone"
                    dataKey="cumulativeAmount"
                    fill="#AACC54"
                    fillOpacity={0.35}
                    stroke="#AACC54"
                    strokeWidth={2}
                    name="Cumulative Amount"
                  />

                  {/* Line for order trend — no dots */}
                  <Line
                    type="monotone"
                    dataKey="orderAmount"
                    stroke="#AACC54"
                    strokeWidth={2}
                    dot={false} // ✅ removes dots
                    activeDot={false} // ✅ prevents hover dots
                    name="Amount Trend"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="bg-white p-4">
          <h3 className="text-gray-500 mb-4 text-lg font-semibold">
            Status Distribution
          </h3>
          <div className="h-72 flex items-center justify-center">
            {pieData.length === 0 || pieData.every((d) => d.value === 0) ? (
              <p className="text-gray-400 text-base">
                No status data available
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    labelLine={true}
                    // label={CustomLabel}
                  >
                    {pieData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={formatNumber} />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ paddingTop: 24 }}
                    className="text-sm"
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="bg-white">
          <h3 className="text-gray-500 mb-4 text-lg font-semibold">
            Order Amount Composition
          </h3>

          <div className="h-72 flex items-center justify-center">
            {topOrders.length === 0 ? (
              <p className="text-gray-400 text-base">No data available</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={topOrders.map((o) => ({
                    name: o.id, // same mapping
                    orderAmount: o.orderAmount,
                    cumulativeAmount: topOrders
                      .slice(0, topOrders.indexOf(o) + 1)
                      .reduce((sum, order) => sum + order.orderAmount, 0),
                  }))}
                  margin={{ top: 10, right: 20, bottom: 20, left: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />

                  <XAxis
                    dataKey="name"
                    label={{
                      value: "ORDER ID",
                      position: "insideBottom",
                      offset: -10,
                      style: { fill: "#444", fontSize: 13 },
                    }}
                    tick={{ fill: "#00000080", fontSize: 12 }}
                  />

                  <YAxis
                    tickFormatter={formatNumber}
                    label={{
                      value: "Amount",
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: "#444", fontSize: 13 },
                    }}
                    tick={{ fill: "#555", fontSize: 12 }}
                  />

                  <Tooltip formatter={formatNumber} />
                  <Legend
                    wrapperStyle={{ paddingTop: 20 }}
                    className="text-sm"
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                  />

                  {/* Individual Order Amount as Bar */}
                  <Bar
                    dataKey="orderAmount"
                    fill="#555"
                    barSize={20}
                    radius={[4, 4, 0, 0]}
                    name="Individual Amount"
                  />
                  <Area
                    type="monotone"
                    dataKey="orderAmount"
                    stackId="a"
                    fill="#AACC54"
                    fillOpacity={0.4}
                    stroke="#AACC54"
                    name="Order Amount Area"
                  />
                  <Line
                    type="monotone"
                    dataKey="cumulativeAmount"
                    stroke="#AACC54"
                    strokeWidth={2}
                    dot={false}
                    activeDot={false}
                    name="Cumulative Amount"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {renderTable()}
      </section>
    </main>
  );
};

export default OffTakerOrderDashboard;
